import { useState, useEffect, useCallback } from 'react';
import {
  ShoppingBag, RefreshCw, Search, CheckCircle, Clock, XCircle,
  User, Mail, Phone, FileText, CreditCard, Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

const statusConfig = {
  pending_payment: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Awaiting Payment' },
  paid: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, label: 'Paid' },
  failed: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Failed' },
};

export default function OrdersView() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [packageFilter, setPackageFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchOrders = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await api.get('/orders');
      setOrders(res.data.orders || []);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const filtered = orders.filter(o => {
    if (packageFilter !== 'all' && o.package !== packageFilter) return false;
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return o.first_name?.toLowerCase().includes(q) ||
      o.last_name?.toLowerCase().includes(q) ||
      o.email?.toLowerCase().includes(q) ||
      o.package_name?.toLowerCase().includes(q);
  });

  const paidCount = orders.filter(o => o.status === 'paid').length;
  const pendingCount = orders.filter(o => o.status === 'pending_payment').length;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div data-testid="orders-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Orders</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {orders.length} total
            {paidCount > 0 && <span className="text-emerald-600 font-medium"> &middot; {paidCount} paid</span>}
            {pendingCount > 0 && <span className="text-amber-500 font-medium"> &middot; {pendingCount} pending</span>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchOrders} data-testid="refresh-orders">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-sm min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search name, email, package..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="order-search"
          />
        </div>
        <Select value={packageFilter} onValueChange={setPackageFilter}>
          <SelectTrigger className="w-[160px]" data-testid="order-package-filter">
            <SelectValue placeholder="All packages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All packages</SelectItem>
            <SelectItem value="starter">Starter</SelectItem>
            <SelectItem value="professional">Professional</SelectItem>
            <SelectItem value="business">Business</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="order-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="pending_payment">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <ShoppingBag className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-1">
              {orders.length === 0 ? 'No orders yet' : 'No matching orders'}
            </h3>
            <p className="text-sm text-slate-400">
              {orders.length === 0 ? 'New orders from the onboarding form will appear here' : 'Try adjusting your search'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-2 space-y-1.5 max-h-[calc(100vh-260px)] overflow-y-auto pr-1">
            {filtered.map(order => {
              const st = statusConfig[order.status] || statusConfig.pending_payment;
              const StIcon = st.icon;
              return (
                <div
                  key={order.id}
                  onClick={() => setSelectedOrder(order)}
                  data-testid={`order-item-${order.id}`}
                  className={cn(
                    "p-3.5 rounded-xl border cursor-pointer transition-all",
                    selectedOrder?.id === order.id
                      ? "bg-orange-50 border-orange-200 shadow-sm"
                      : "bg-white border-slate-200 hover:border-slate-300"
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-semibold text-slate-900 truncate">
                          {order.first_name} {order.last_name}
                        </span>
                        <Badge className={cn("text-[10px]", st.color)}>
                          <StIcon className="w-2.5 h-2.5 mr-0.5" />{st.label}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 truncate">{order.email}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {order.package_name} &middot; £{order.amount}
                      </p>
                    </div>
                    <p className="text-[10px] text-slate-400 whitespace-nowrap">{formatDate(order.created_at)}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="lg:col-span-3">
            {selectedOrder ? (
              <Card data-testid="order-detail-panel">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl">{selectedOrder.first_name} {selectedOrder.last_name}</CardTitle>
                      <CardDescription className="mt-1">{selectedOrder.profession || 'No profession specified'}</CardDescription>
                    </div>
                    <Badge className={cn("text-sm px-3 py-1", (statusConfig[selectedOrder.status] || statusConfig.pending_payment).color)}>
                      {(statusConfig[selectedOrder.status] || statusConfig.pending_payment).label}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <a href={`mailto:${selectedOrder.email}`} className="text-orange-600 hover:underline">{selectedOrder.email}</a>
                    </div>
                    {selectedOrder.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-700">{selectedOrder.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <CreditCard className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-700">
                        {selectedOrder.package_name}
                        {selectedOrder.amount > 0 ? ` · £${selectedOrder.amount} setup` : ' · Free setup'}
                        {selectedOrder.monthly_price ? ` · £${selectedOrder.monthly_price}/mo` : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-700">{new Date(selectedOrder.created_at).toLocaleString('en-GB')}</span>
                    </div>
                  </div>

                  {selectedOrder.description && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                      <p className="text-xs text-slate-500 mb-1 font-medium uppercase tracking-wide">About them</p>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedOrder.description}</p>
                    </div>
                  )}

                  {selectedOrder.site_id && (
                    <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                      <p className="text-xs text-emerald-600 mb-1 font-medium">Draft site created</p>
                      <p className="text-sm text-emerald-800">Site ID: {selectedOrder.site_id}</p>
                    </div>
                  )}

                  <div className="text-xs text-slate-400">Order ID: {selectedOrder.id}</div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-20 text-center">
                  <ShoppingBag className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <p className="text-sm text-slate-400">Select an order to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
